/**
 * <biosphere-buy-tiers> — enhancement for snippets/biosphere-buy-tiers.liquid.
 *
 * The cards are a view onto the form's quantity input, not a second control: picking
 * a tier writes that quantity and lets the ported quantity component react, and
 * moving the stepper picks the matching tier back.
 *
 * No money is computed here — Liquid formatted every price onto its radio.
 */
class BiosphereBuyTiers extends HTMLElement {
  #inputs = [];
  #quantity;
  #scope;
  #group;
  #prices;
  #saving;
  #shipping;
  #abort;

  connectedCallback() {
    this.#inputs = [...this.querySelectorAll(".biosphere-buy-tiers__input")];

    // The tiers sit beside the product form rather than inside it, so the form is found
    // by the id Liquid wrote. Without it there is nothing to drive, so the cards stay
    // hidden and the ported stepper stands alone.
    const form = document.getElementById(this.dataset.formId);
    this.#quantity = form?.querySelector('input[name="quantity"]');
    if (!this.#quantity || this.#inputs.length === 0) return;

    // The savings bar and the ship nudge sit outside this element — the subscribe card
    // comes between them — so they are found by the scope id Liquid stamped on both.
    this.#scope = CSS.escape(this.dataset.scope ?? "");

    // The server's price for every quantity a card does not cover, keyed by quantity.
    // Each entry has exactly the shape of a tier radio's dataset, so both paths hand
    // the same object on.
    this.#prices = Object.fromEntries(
      [...this.querySelectorAll("[data-tier-quote]")].map((quote) => [
        quote.dataset.tierQuote,
        quote.dataset,
      ]),
    );

    this.#saving = document.querySelector(
      `[data-tier-saving="${this.#scope}"]`,
    );
    this.#shipping = document.querySelector(
      `[data-tier-shipping="${this.#scope}"]`,
    );

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#promoteLabels();
    this.addEventListener(
      "click",
      (event) => {
        const control = event.target.closest?.(
          '.biosphere-buy-tiers__tier[role="radio"]',
        );
        const input = control?.querySelector('input[type="radio"]');
        if (input && event.target !== input) {
          control.focus();
          input.click();
        }
      },
      { signal },
    );
    this.addEventListener("keydown", (event) => this.#onKeydown(event), {
      signal,
    });

    // The subscribe card shares this radio name, so picking it unchecks every tier
    // without firing a change on any of them. The group is watched at the document and
    // filtered by name — the radios sit outside the form, so there is no form to
    // listen on.
    this.#group = this.#inputs[0].name;
    const group = this.#group;
    document.addEventListener(
      "change",
      (event) => {
        if (event.target?.name !== group) return;
        this.#syncChecked();
        const checked = this.#inputs.find((input) => input.checked);
        if (checked) this.#apply(checked);
      },
      { signal },
    );

    // The stepper is still a real control; when it moves off a tier's quantity the
    // selection follows rather than sitting on a lie.
    document.addEventListener(
      "biosphere:quantity:change",
      (event) => this.#sync(event),
      { signal },
    );

    // A variant change rebuilds the button from the server, which renders the resting
    // state — one pack, no tier — so whatever is picked has to be written over it
    // again. On the next frame, because the button is rebuilt by a listener on this
    // same event.
    document.addEventListener(
      "biosphere:variant-picker:update",
      () =>
        requestAnimationFrame(() => {
          const checked = this.#inputs.find((input) => input.checked);
          if (checked) this.#apply(checked, false);
        }),
      { signal },
    );

    const checked = this.#inputs.find((input) => input.checked);
    if (checked) this.#apply(checked, false);

    this.hidden = false;
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * Every tier its own tab stop, through the cards.
   *
   * The platform will not give this with radios: an unchecked radio whose group has a
   * checked one is out of sequential focus navigation, and tabindex does not override
   * it. So the card becomes the radio for keyboard and AT, and the real input stays
   * underneath, out of the accessibility tree so each tier is announced once.
   *
   * Done to the markup, not instead of it — with no script these are labels over radios.
   */
  #promoteLabels() {
    for (const input of this.#inputs) {
      let label = input.closest(".biosphere-buy-tiers__tier");
      if (!label) continue;
      if (label.tagName === "LABEL") {
        const control = document.createElement("div");
        for (const attribute of label.attributes) {
          control.setAttribute(attribute.name, attribute.value);
        }
        control.append(...label.childNodes);
        label.replaceWith(control);
        label = control;
      }

      label.setAttribute("role", "radio");
      label.setAttribute("aria-checked", String(input.checked));
      label.setAttribute("aria-label", label.textContent.trim());
      label.tabIndex = 0;

      input.setAttribute("aria-hidden", "true");
      input.tabIndex = -1;
    }
  }

  /** Mirror each card's aria-checked onto its input's real state. */
  #syncChecked() {
    for (const input of this.#inputs) {
      input
        .closest(".biosphere-buy-tiers__tier")
        ?.setAttribute("aria-checked", String(input.checked));
    }
  }

  /**
   * Space picks the focused tier; the arrows move and pick, which is what the native
   * radios did before the cards took over the tab order.
   */
  #onKeydown(event) {
    const label = event.target.closest?.(".biosphere-buy-tiers__tier");
    if (!label || label.getAttribute("role") !== "radio") return;

    const labels = this.#inputs
      .map((input) => input.closest(".biosphere-buy-tiers__tier"))
      .filter(Boolean);
    const from = labels.indexOf(label);
    let target;

    if (event.key === " " || event.key === "Enter") target = from;
    else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      target = from + 1;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      target = from - 1;
    } else return;

    event.preventDefault();

    const next = labels[(target + labels.length) % labels.length];
    const input = next?.querySelector('input[type="radio"]');
    next?.focus();
    if (!input || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /** Write the tier's quantity to the real input and label the button. */
  #apply(input, notify = true) {
    const quantity = input.dataset.quantity;
    if (notify && this.#quantity.value !== quantity) {
      this.#quantity.value = quantity;
      this.#quantity.dispatchEvent(new Event("change", { bubbles: true }));
    }

    this.#render(input.dataset);
  }

  /**
   * Quote a quantity no card covers. The cards stop at the last step because the one
   * above is open-ended, but the buyer can still step into it. Read, never worked out:
   * the server priced every reachable quantity, and past the end of the table it shows
   * none rather than the last it knew.
   */
  #quote(quantity) {
    const state = this.#prices?.[quantity];
    this.#render(state ? { ...state, quantity } : {});
  }

  /**
   * Put one set of server-rendered strings into the page. This copies; it never builds
   * or computes a price, and an empty set clears the lot.
   *
   * Every slot carrying this scope — main button and sticky bar — so the two cannot
   * quote different figures. Looked up each call, not cached: the sticky bar re-renders
   * itself on a variant change.
   */
  #render(state) {
    document
      .querySelectorAll(`[data-tier-price="${this.#scope}"]`)
      .forEach((slot) => {
        slot.textContent = state.price ?? "";
      });

    this.#line(this.#saving, state.saving);
    this.#line(this.#shipping, state.shipping);

    if (this.#shipping) {
      this.#shipping.classList.toggle(
        "biosphere-product-hero__nudge--free",
        state.shippingFree === true || state.shippingFree === "true",
      );
    }

    // Announced rather than written into anything — what else cares about the pack is
    // not this component's business. The sticky bar listens; nothing here knows it
    // exists.
    this.dispatchEvent(
      new CustomEvent("biosphere:buy-tiers:change", {
        bubbles: true,
        detail: {
          scope: this.dataset.scope,
          quantity: state.quantity,
          label: state.label,
          price: state.price,
        },
      }),
    );
  }

  /**
   * Show a line with its text, or take it away when there is none. The text goes into
   * its own slot rather than the line itself: the line also holds an icon, and
   * writing over the whole element would take that with it.
   */
  #line(element, text) {
    if (!element) return;
    const slot = element.querySelector("[data-tier-text]") ?? element;
    slot.textContent = text ?? "";
    element.hidden = !text;
  }

  /** Follow the stepper: select the tier matching it, or none. */
  #sync(event) {
    // Only when a tier is what is selected. The subscribe card is in this radio group,
    // and while it holds the selection the quantity is its business: grabbing the
    // matching tier here would uncheck the card through the browser's own radio
    // behaviour, turning a chosen subscription back into a one-off.
    const owner = document.querySelector(
      `input[name="${this.#group}"]:checked`,
    );
    if (owner && !this.#inputs.includes(owner)) return;

    const quantity = String(event.detail?.quantity ?? this.#quantity.value);
    const match = this.#inputs.find(
      (input) => input.dataset.quantity === quantity,
    );
    const moved = !match?.checked;

    this.#inputs.forEach((input) => {
      input.checked = input === match;
    });

    // Off the ladder: no card is checked, so nothing is selected and the quantity is
    // quoted straight from the table.
    if (!match) {
      this.#quote(quantity);
      return;
    }

    // Checking a radio in code unchecks the rest of its group without firing anything,
    // and the subscribe card is in this group: left silent it goes on believing it is
    // selected, so the page would show a tier picked and a subscription still being
    // sold. The announcement comes back through the listener above, which applies the
    // tier.
    if (moved) {
      match.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    this.#apply(match, false);
  }
}

if (!customElements.get("biosphere-buy-tiers")) {
  customElements.define("biosphere-buy-tiers", BiosphereBuyTiers);
}
